import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { useClaimDeleteReasonSelect } from "../../../admin/Dictionaries/hooks/getClaimDeleteReasonByClient";
import { useDemandOutFormatSelect } from "../../../admin/Dictionaries/hooks/getDemandOutFormatByClient";
import { ClaimClosedFormDto } from "../../types";
import { convertUtc } from "../../../../utils/dateUtils";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { getClaim, updateClaimClose } from "../../api/ClaimAPI";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useDemandDerivationSelect } from "../../../admin/Dictionaries/hooks/getDemandDerivationByClient";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { useQueryClient } from "@tanstack/react-query";
import { claimClosedModalSchema } from "./claimClosedModalSchema";
import { useNavigate } from "react-router-dom";
import { useUsersSelect } from "../../../../hooks/getUsersByClient";

type ClaimClosedModalProps = {
    show: boolean;
    claimId: number | null;
    onClose: () => void;
};

export default function ClaimClosedModal({
    show,
    claimId,
    onClose,
}: ClaimClosedModalProps) {
    const { t } = useTranslation(["claim", "common"]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

    const { data: claimDeleteReasons } = useClaimDeleteReasonSelect({});
    const { data: claimOutFormats } = useDemandOutFormatSelect({});
    const { data: demandDerivations } = useDemandDerivationSelect({});
    const { data: users } = useUsersSelect({});

    const [claimClosedFormDto, setClaimClosedFormDto] = useState<ClaimClosedFormDto>({
        claimDeleteReasonId: null,
        closingDate: null
    });
    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    const handleChange = (field: keyof ClaimClosedFormDto, value: any) => {
        setClaimClosedFormDto(prev => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handleDateChange = (field: keyof ClaimClosedFormDto, date: Date | null) => {
        if (date) {
            const utcDate = convertUtc(date);
            handleChange(field, utcDate?.getTime() ?? null);
        } else {
            handleChange(field, null);
        }
    };


    useEffect(() => {
        if (!show || !claimId) {
            return;
        }

        loadData();
    }, [show, claimId, t]);

    const loadData = async () => {
        try {
            if (!claimId)
                return;

            setIsLoading(true);
            setErrors({});
            const claim = await getClaim(claimId);
            claimClosedFormDto.claimInOutFormatOutputId = claim.claimInOutFormatOutputId ;
            claimClosedFormDto.demandDerivationId = claim.demandDerivationId ;
            claimClosedFormDto.resolution = claim.resolution;
            claimClosedFormDto.idUserRelated = claim.idUserRelated;
            if (claim.deliveryDate == null) {
                showAlert({
                    message: t("claim:modalClose.messageWarningNotExistResponseDate"),
                    type: 'warning',
                    duration: 8000
                });
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: 'danger',
                duration: 8000
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);

        const schema = claimClosedModalSchema(t);        
        try {
            await schema.validate(claimClosedFormDto, { abortEarly: false });
        } catch (err: any) {
            setIsSaving(false);
            const validationErrors: { [key: string]: string | undefined } = {};
            if (err?.inner && Array.isArray(err.inner) && err.inner.length > 0) {
                err.inner.forEach((error: any) => {
                    if (error.path) {
                        validationErrors[error.path] = error.message;
                    }
                });
            } else if (err?.path) {
                validationErrors[err.path] = err.message;
            }
            setErrors(validationErrors);
            return;
        }

        try {
            if (!claimId)
                return;

            await updateClaimClose(claimId, claimClosedFormDto);
            onClose();
            
            queryClient.invalidateQueries({
                queryKey: ['getHistoricClaims'],
                exact: false,
                refetchType: 'all',
            });
            navigate('/claims/historic', {
                state: { message: t("claim:modalClose.successMessage"), shouldRefresh: true }
            });            
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message || t("common:error");
            showAlert({
                message: errorMessage,
                type: 'danger',
                duration: 8000
            });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered className="improvement-plan-finalize-modal" size="xl">
            <Modal.Header closeButton>
                <Modal.Title>{t("claim:modalClose.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isLoading ? (
                    <div className="text-center py-4">
                        <Spinner animation="border" />
                    </div>
                ) : (
                       <>
                        {isVisible && (
                            <AlertMessage
                                message={alert!.message}
                                type={alert!.type}
                                onClose={hideAlert}
                            />
                        )}
                        <Form>
                            <Row>
                                <Col xs={8}>
                                    <Form.Group>
                                        <Form.Label>{t("claim:modalClose.labelClaimDeleteReason")}</Form.Label>
                                        <Form.Select
                                            value={claimClosedFormDto?.claimDeleteReasonId ?? ""}
                                            isInvalid={!!errors.claimDeleteReasonId}
                                            onChange={(event) => {
                                                setClaimClosedFormDto((prev) => {
                                                    if (!prev) {
                                                        return prev;
                                                    }

                                                    return {
                                                        ...prev,
                                                        claimDeleteReasonId: event.target.value ? Number(event.target.value) : 0
                                                    };
                                                });
                                            }}
                                        >
                                            <option value=""></option>
                                            {claimDeleteReasons?.map((type) => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {errors.claimDeleteReasonId}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col xs={4}>
                                    <InputDatePicker
                                        value={claimClosedFormDto?.closingDate ? new Date(claimClosedFormDto.closingDate) : null}
                                        onChange={(date) => handleDateChange("closingDate", date)}
                                        label={t("claim:modalClose.labelClosingDate")}
                                    />
                                    {errors.closingDate && (
                                        <div className="invalid-feedback d-block">
                                            {errors.closingDate}
                                        </div>
                                    )}
                                </Col>
                            </Row>

                            <Row className="mt-4">
                                <Col xs={12}>
                                    <h5 className="mb-1">{t("claim:modalClose.titleClostingData")}</h5>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={3} md={3}>
                                    <Form.Label>{t("claim:detail.labelUserRelated")}</Form.Label>
                                    <Form.Select
                                        className="form-control"
                                        value={claimClosedFormDto?.idUserRelated ?? ""}
                                        onChange={(e) => handleChange("idUserRelated", e.target.value)}
                                    >
                                        <option value=""></option>
                                        {users?.map((user) => (
                                            <option key={user.id} value={user.id}>
                                                {user.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={8} md={8} className="mb-2 mb-md-0">
                                    <Form.Label>{t("claim:modalClose.labelRequestOutputFomat")}</Form.Label>
                                    <Form.Select
                                        className="form-control"
                                        value={claimClosedFormDto?.claimInOutFormatOutputId ?? ""}
                                        onChange={(e) => handleChange("claimInOutFormatOutputId", e.target.value)}
                                    >
                                        <option value=""></option>
                                        {claimOutFormats?.map((type) => (
                                            <option key={type.id} value={type.id}>
                                                {type.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={8} md={8} className="mb-2 mb-md-0">
                                        <Form.Label>{t("claim:modalClose.labelRequestDerivation")}</Form.Label>
                                        <Form.Select
                                            className="form-control"
                                            value={claimClosedFormDto?.demandDerivationId ?? ""}
                                            onChange={(e) => handleChange("demandDerivationId", e.target.value)}
                                        >
                                            <option value=""></option>
                                            {demandDerivations?.map((type) => (
                                                <option key={type.id} value={type.id}>
                                                    {type.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col xs={8} md={8} className="mb-2 mb-md-0">
                                        <Form.Label>{t("claim:modalClose.labelRequestResolution")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={claimClosedFormDto?.resolution ?? ""}
                                            onChange={(e) => handleChange("resolution", e.target.value)}
                                        />
                                    </Col>
                                </Row>
                    </Form>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-success" onClick={onClose} disabled={isSaving || isLoading}>
                    {t("common:buttons.buttonCancel")}
                </Button>
                <Button variant="success" onClick={handleSave} disabled={isSaving || isLoading}>
                    {isSaving && (
                        <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            role="status"
                            aria-hidden="true"
                            className="me-2"
                        />
                    )}
                    {t("common:buttons.buttonEnd")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

